import React, { useContext, useEffect, useState, useRef } from 'react'
import PageTitle from '../../../Components/PageTitle'
import { ConfigContext } from '../../../Context/ConfigContext'
import { SubmitBtn } from '../../../Components/InputElements'
import { ContentLoader } from '../../../Components/Shimmer'
import axios from 'axios'
import Swal from 'sweetalert2'
import { NavLink, useNavigate, useParams } from 'react-router-dom'

const ManageProducts = () => {
    const { primaryColor, apiHeaderJson, apiURL, apiHeaderFile } = useContext(ConfigContext)
    const headersFile = apiHeaderFile;
    const { product_id = null } = useParams();

    const [productTitle, setProductTitle] = useState('')
    const [productSpecification, setProductSpecification] = useState('')
    const [productPhoto, setProductPhoto] = useState(null)
    const [productSignaturePhoto, setProductSignaturePhoto] = useState(null)
    const [productPublishStatus, setProductPublishStatus] = useState(0)
    const [productTrendingStatus, setProductTrendingStatus] = useState(0)
    const [productSignatureStatus, setProductSignatureStatus] = useState(0)
    const [productId, setProductId] = useState(product_id)

    const [imagePreview, setImagePreview] = useState('')
    const [signatureImagePreview, setSignatureImagePreview] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)
    const [errors, setErrors] = useState({})
    const [isDragOver, setIsDragOver] = useState(false);
    const [isSignatureDragOver, setIsSignatureDragOver] = useState(false);
    const [isInfoLoading, setIsInfoLoading] = useState(false);

    const fileInputRef = useRef(null)
    const signatureFileInputRef = useRef(null)
    const dropZoneRef = useRef(null)
    const signatureDropZoneRef = useRef(null)
    const navigate = useNavigate();

    const validateForm = () => {
        const errors = {}

        if (!productTitle.trim()) {
            errors.productTitle = "Product title is required"
        }
        if (!productSpecification.trim()) {
            errors.productSpecification = "Product specification is required"
        }
        if (!productId && !productPhoto) {
            errors.productPhoto = "Product photo is required"
        }
        if (productSignaturePhoto) {
            const sigValidation = validateImageFile(productSignaturePhoto)
            if (!sigValidation.isValid) {
                errors.productSignaturePhoto = sigValidation.message
            }
        }

        setErrors(errors)
        return Object.keys(errors).length === 0
    }

    const validateImageFile = (file) => {
        const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
        if (!validImageTypes.includes(file.type)) {
            return {
                isValid: false,
                message: 'Please select a valid image file (JPEG, JPG, PNG, WEBP only).'
            }
        }

        const maxSize = 1 * 1024 * 1024
        if (file.size > maxSize) {
            return {
                isValid: false,
                message: 'Image size must be less than 1MB.'
            }
        }

        return { isValid: true }
    }

    const resetForm = () => {
        setProductTitle("")
        setProductSpecification("")
        setProductPhoto(null)
        setProductSignaturePhoto(null)
        setImagePreview("")
        setSignatureImagePreview("")
        setProductPublishStatus(0)
        setProductTrendingStatus(0)
        setProductSignatureStatus(0)
        setProductId('')
        setErrors({})
        setIsDragOver(false);
        setIsSignatureDragOver(false);
        if (productId) {
            navigate('/Masters/ProductList')
        }
    }

    // Generic drag handlers
    const handleDragOver = (e, isSignature = false) => {
        e.preventDefault()
        e.stopPropagation()
        if (isSignature) setIsSignatureDragOver(true)
        else setIsDragOver(true)
    }

    const handleDragLeave = (e, isSignature = false) => {
        e.preventDefault()
        e.stopPropagation()
        if (isSignature) setIsSignatureDragOver(false)
        else setIsDragOver(false)
    }

    const handleDrop = (e, isSignature = false) => {
        e.preventDefault()
        e.stopPropagation()
        if (isSignature) setIsSignatureDragOver(false)
        else setIsDragOver(false)

        const files = e.dataTransfer.files
        if (files && files.length > 0) {
            const file = files[0]
            const validation = validateImageFile(file)

            if (validation.isValid) {
                if (isSignature) processSignatureImageFile(file)
                else processImageFile(file)
            } else {
                Swal.fire('Error!', validation.message, 'error')
            }
        }
    }

    const handleFileSelect = (e, isSignature = false) => {
        const file = e.target.files[0]
        if (file) {
            const validation = validateImageFile(file)

            if (validation.isValid) {
                if (isSignature) processSignatureImageFile(file)
                else processImageFile(file)
            } else {
                Swal.fire('Error!', validation.message, 'error')
                if (isSignature && signatureFileInputRef.current) {
                    signatureFileInputRef.current.value = ''
                } else if (!isSignature && fileInputRef.current) {
                    fileInputRef.current.value = ''
                }
            }
        }
    }

    const processImageFile = (file) => {
        setProductPhoto(file)
        const previewUrl = URL.createObjectURL(file)
        setImagePreview(previewUrl)

        if (errors.productPhoto) {
            setErrors({ ...errors, productPhoto: "" })
        }
    }

    const processSignatureImageFile = (file) => {
        setProductSignaturePhoto(file)
        const previewUrl = URL.createObjectURL(file)
        setSignatureImagePreview(previewUrl)

        if (errors.productSignaturePhoto) {
            setErrors({ ...errors, productSignaturePhoto: "" })
        }
    }

    const handleDropZoneClick = (isSignature = false) => {
        if (isSignature) signatureFileInputRef.current?.click()
        else fileInputRef.current?.click()
    }

    const removeImage = (isSignature = false) => {
        if (isSignature) {
            setProductSignaturePhoto(null)
            setSignatureImagePreview('')
            if (signatureFileInputRef.current) {
                signatureFileInputRef.current.value = ''
            }
        } else {
            setProductPhoto(null)
            setImagePreview('')
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validateForm()) return

        if (productPhoto) {
            const validation = validateImageFile(productPhoto)
            if (!validation.isValid) {
                Swal.fire('Error!', validation.message, 'error')
                return
            }
        }
        if (productSignaturePhoto) {
            const validation = validateImageFile(productSignaturePhoto)
            if (!validation.isValid) {
                Swal.fire('Error!', validation.message, 'error')
                return
            }
        }

        setSubmitLoading(true)
        try {
            const formData = new FormData()
            formData.append('product_title', productTitle)
            formData.append('product_specification', productSpecification)
            formData.append('product_publish_status', productPublishStatus)
            formData.append('product_trending_status', productTrendingStatus)
            formData.append('product_signature_status', productSignatureStatus)

            if (productPhoto) {
                formData.append('product_photo', productPhoto)
            }
            if (productSignaturePhoto) {
                formData.append('product_signature_photo', productSignaturePhoto)
            }

            if (productId) {
                formData.append('product_id', productId)
            }

            const response = await axios.post(`${apiURL}Masters/CreateProduct`, formData, { headers: headersFile })
            const { message, success } = response.data

            if (success) {
                resetForm();
                Swal.fire('Success!', message, 'success')
            }
        } catch (error) {
            console.log("error", error)
            Swal.fire('Error!', 'Something went wrong', 'error')
        } finally {
            setSubmitLoading(false)
        }
    }

    const GetProductInfo = async () => {
        try {
            setIsInfoLoading(true);
            const response = await axios.get(`${apiURL}Masters/GetProductInfo`, { params: { product_id }, headers: apiHeaderJson })
            const { message, success, data } = response.data;
            const product_info = data[0] || {};
            if (success) {
                setProductTitle(product_info.product_title || '');
                setProductSpecification(product_info.product_specification || '');
                setProductPublishStatus(product_info.product_publish_status || 0);
                setProductTrendingStatus(product_info.product_trending_status || 0);
                setProductSignatureStatus(product_info.product_signature_status || 0);
                setImagePreview(product_info.product_image_url || '');
                setSignatureImagePreview(product_info.product_signature_image_url || '');
            } else {
                Swal.fire('Error!', message || 'Something went wrong', 'error')
            }
        } catch (error) {
            console.log("error", error)
            Swal.fire('Error!', 'Something went wrong', 'error')
        } finally {
            setIsInfoLoading(false);
        }
    }

    useEffect(() => {
        product_id && GetProductInfo();
    }, [product_id]);

    return (
        <>
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <PageTitle title="Manage Products" primary="Products" />
                        {isInfoLoading && <ContentLoader />}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-header d-flex flex-wrap justify-content-between align-items-center" style={{ backgroundColor: primaryColor }}>
                                        <h4 className="card-title mb-0 text-white">
                                            {productId ? 'Update Product' : 'New Product'}
                                        </h4>
                                        <NavLink type='button' to="/Masters/ProductList" className="btn btn-sm btn-light"><i className='ri-menu-2-line' /></NavLink>
                                    </div>
                                    <div className="card-body">
                                        <form onSubmit={handleSubmit}>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="productTitle" className="form-label">
                                                        Product Title: <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="productTitle"
                                                        name="productTitle"
                                                        value={productTitle}
                                                        onChange={(e) => {
                                                            setProductTitle(e.target.value);
                                                            if (errors.productTitle) {
                                                                setErrors({ ...errors, productTitle: "" })
                                                            }
                                                        }}
                                                        className={`form-control ${errors.productTitle ? 'is-invalid' : ''}`}
                                                        placeholder="Enter Product Title"
                                                    />
                                                    {errors.productTitle && <div className="invalid-feedback">{errors.productTitle}</div>}
                                                </div>

                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="productSpecification" className="form-label">
                                                        Product Specification: <span className="text-danger">*</span>
                                                    </label>
                                                    <textarea
                                                        id="productSpecification"
                                                        name="productSpecification"
                                                        value={productSpecification}
                                                        onChange={(e) => {
                                                            setProductSpecification(e.target.value);
                                                            if (errors.productSpecification) {
                                                                setErrors({ ...errors, productSpecification: "" })
                                                            }
                                                        }}
                                                        className={`form-control ${errors.productSpecification ? 'is-invalid' : ''}`}
                                                        placeholder="Enter product specifications..."
                                                        rows="5"
                                                    />
                                                    {errors.productSpecification && <div className="invalid-feedback">{errors.productSpecification}</div>}
                                                </div>

                                                {/* Product Photo Upload */}
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="productPhoto" className="form-label">
                                                        Product Photo: <span className="text-danger">*</span>
                                                    </label>

                                                    <input
                                                        type="file"
                                                        id="productPhoto"
                                                        name="productPhoto"
                                                        accept="image/jpeg, image/jpg, image/png, image/webp"
                                                        onChange={(e) => handleFileSelect(e, false)}
                                                        className="d-none"
                                                        ref={fileInputRef}
                                                    />

                                                    <div
                                                        ref={dropZoneRef}
                                                        className={`border-2 border-dashed rounded p-4 text-center cursor-pointer ${isDragOver
                                                            ? 'border-primary bg-light'
                                                            : 'border-gray-300'
                                                            } ${errors.productPhoto ? 'border-danger' : ''}`}
                                                        onDragOver={(e) => handleDragOver(e, false)}
                                                        onDragLeave={(e) => handleDragLeave(e, false)}
                                                        onDrop={(e) => handleDrop(e, false)}
                                                        onClick={() => handleDropZoneClick(false)}
                                                        style={{
                                                            borderStyle: isDragOver ? 'solid' : 'dashed',
                                                            transition: 'all 0.3s ease'
                                                        }}
                                                    >
                                                        {imagePreview ? (
                                                            <div className="position-relative">
                                                                <img
                                                                    src={imagePreview}
                                                                    alt="Product Preview"
                                                                    className="img-thumbnail mx-auto"
                                                                    style={{
                                                                        maxWidth: '200px',
                                                                        maxHeight: '200px',
                                                                        display: 'block'
                                                                    }}
                                                                />
                                                                <div className="mt-2 small text-muted">
                                                                    {productPhoto && `Size: ${formatFileSize(productPhoto.size)}`}
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        removeImage(false)
                                                                    }}
                                                                    style={{ zIndex: 10 }}
                                                                >
                                                                    <i className="ri-close-line"></i>
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                <i
                                                                    className="ri-upload-cloud-2-line display-4 text-muted mb-2"
                                                                    style={{ fontSize: '3rem' }}
                                                                ></i>
                                                                <p className="mb-1">
                                                                    <span className="text-primary">Click to upload</span> or drag and drop
                                                                </p>
                                                                <p className="text-muted small mb-0">
                                                                    PNG, JPG, JPEG, WEBP (Max. 1MB)
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {errors.productPhoto && (
                                                        <div className="invalid-feedback d-block">{errors.productPhoto}</div>
                                                    )}

                                                    <small className="text-muted">
                                                        {productId ? 'Select new photo to update existing one' : 'Please select a photo for the product'}
                                                    </small>
                                                </div>

                                                {/* Product Signature Photo Upload */}
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="productSignaturePhoto" className="form-label">
                                                        Product Signature Photo:
                                                    </label>

                                                    <input
                                                        type="file"
                                                        id="productSignaturePhoto"
                                                        name="productSignaturePhoto"
                                                        accept="image/jpeg, image/jpg, image/png, image/webp"
                                                        onChange={(e) => handleFileSelect(e, true)}
                                                        className="d-none"
                                                        ref={signatureFileInputRef}
                                                    />

                                                    <div
                                                        ref={signatureDropZoneRef}
                                                        className={`border-2 border-dashed rounded p-4 text-center cursor-pointer ${isSignatureDragOver
                                                            ? 'border-primary bg-light'
                                                            : 'border-gray-300'
                                                            } ${errors.productSignaturePhoto ? 'border-danger' : ''}`}
                                                        onDragOver={(e) => handleDragOver(e, true)}
                                                        onDragLeave={(e) => handleDragLeave(e, true)}
                                                        onDrop={(e) => handleDrop(e, true)}
                                                        onClick={() => handleDropZoneClick(true)}
                                                        style={{
                                                            borderStyle: isSignatureDragOver ? 'solid' : 'dashed',
                                                            transition: 'all 0.3s ease'
                                                        }}
                                                    >
                                                        {signatureImagePreview ? (
                                                            <div className="position-relative">
                                                                <img
                                                                    src={signatureImagePreview}
                                                                    alt="Signature Preview"
                                                                    className="img-thumbnail mx-auto"
                                                                    style={{
                                                                        maxWidth: '200px',
                                                                        maxHeight: '200px',
                                                                        display: 'block'
                                                                    }}
                                                                />
                                                                <div className="mt-2 small text-muted">
                                                                    {productSignaturePhoto && `Size: ${formatFileSize(productSignaturePhoto.size)}`}
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        removeImage(true)
                                                                    }}
                                                                    style={{ zIndex: 10 }}
                                                                >
                                                                    <i className="ri-close-line"></i>
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                <i
                                                                    className="ri-upload-cloud-2-line display-4 text-muted mb-2"
                                                                    style={{ fontSize: '3rem' }}
                                                                ></i>
                                                                <p className="mb-1">
                                                                    <span className="text-primary">Click to upload</span> or drag and drop
                                                                </p>
                                                                <p className="text-muted small mb-0">
                                                                    PNG, JPG, JPEG, WEBP (Max. 1MB)
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {errors.productSignaturePhoto && (
                                                        <div className="invalid-feedback d-block">{errors.productSignaturePhoto}</div>
                                                    )}

                                                    <small className="text-muted">
                                                        Optional signature photo
                                                    </small>
                                                </div>

                                                {/* Status toggles */}
                                                <div className="col-md-12">
                                                    <div className="row">
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">Publish Status:</label>
                                                            <div className="form-check form-switch">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    role="switch"
                                                                    id="productPublishStatus"
                                                                    checked={productPublishStatus === 1}
                                                                    onChange={(e) => setProductPublishStatus(e.target.checked ? 1 : 0)}
                                                                />
                                                                <label className="form-check-label" htmlFor="productPublishStatus">
                                                                    {productPublishStatus === 1 ? 'Published' : 'Not Published'}
                                                                </label>
                                                            </div>
                                                        </div>

                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">Trending Status:</label>
                                                            <div className="form-check form-switch">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    role="switch"
                                                                    id="productTrendingStatus"
                                                                    checked={productTrendingStatus === 1}
                                                                    onChange={(e) => setProductTrendingStatus(e.target.checked ? 1 : 0)}
                                                                />
                                                                <label className="form-check-label" htmlFor="productTrendingStatus">
                                                                    {productTrendingStatus === 1 ? 'Trending' : 'Not Trending'}
                                                                </label>
                                                            </div>
                                                        </div>

                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">Signature Status:</label>
                                                            <div className="form-check form-switch">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    role="switch"
                                                                    id="productSignatureStatus"
                                                                    checked={productSignatureStatus === 1}
                                                                    onChange={(e) => setProductSignatureStatus(e.target.checked ? 1 : 0)}
                                                                />
                                                                <label className="form-check-label" htmlFor="productSignatureStatus">
                                                                    {productSignatureStatus === 1 ? 'Signature' : 'Not Signature'}
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-12 mb-3">
                                                    <div className='d-flex align-items-end justify-content-end gap-3 mt-3'>
                                                        {productId ? (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-light btn-label right me-auto"
                                                                    onClick={resetForm}
                                                                >
                                                                    Cancel
                                                                    <i className="ri-arrow-right-line label-icon align-middle fs-16" />
                                                                </button>
                                                                <SubmitBtn
                                                                    icon="ri-save-line"
                                                                    text="Update Product"
                                                                    onClick={handleSubmit}
                                                                    type="primary"
                                                                />
                                                            </>
                                                        ) : (
                                                            <SubmitBtn
                                                                disabled={submitLoading}
                                                                text={submitLoading ? 'Saving Product...' : 'Save Product'}
                                                                type="primary"
                                                                onClick={handleSubmit}
                                                                icon="ri-save-line"
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ManageProducts